/**
 * ProfilePage(+Person) JSON-LD 생성기.
 *
 * 봇 전용 SEO 서버(seo-geo-server)가 유저 게시글 페이지에 내보내는 ProfilePage 구조화 데이터와
 * 동일한 스키마·필드 출처를 FE에서도 재현하기 위한 순수 함수입니다.
 * (dynamic rendering parity: "봇 = 유저 콘텐츠의 prerender")
 *
 * ⚠️ 여기서 만드는 값은 페이지에 실제 표시되는 콘텐츠(name/카운트 등)와 일치해야 합니다.
 * 불일치 시 구조화 데이터 스팸으로 간주되어 리치 결과가 박탈될 수 있습니다.
 */

/** ProfilePage.mainEntity 의 주체 타입. 기관 계정이면 'Organization'. */
export type ProfileEntityType = 'Person' | 'Organization';

export type BuildProfilePageJsonLdParams = {
  /** 프로필 정규 URL (/users/{id}/@{slug}). @id 와 url 에 사용 */
  profileUrl: string;
  /** 표시되는 유저 이름 (profile.name) */
  name: string;
  /** 유저 ID (identifier) */
  userId: string | number;
  /** meta description 과 동일한 문구 */
  description: string;
  /** 프로필 썸네일 URL. 없으면 image 키 생략 */
  thumbnailUrl?: string | null;
  /** 게시글 totalCount (WriteAction interactionCount) */
  totalCount: number;
  /** 최신 게시글 createdAt (UTC ISO). 없으면 dateModified 키 생략 */
  dateModified?: string | null;
  /**
   * 멘토(지식공유자) 정보. isKo && mentor.isActive 일 때만 호출부에서 전달.
   * 전달되면 jobTitle / worksFor 키를 포함합니다.
   */
  mentor?: {
    job?: string | null;
    companyName?: string | null;
  } | null;
  /** mainEntity 타입. 기본값 'Person' */
  entityType?: ProfileEntityType;
};

type InteractionCounter = {
  '@type': 'InteractionCounter';
  interactionType: 'https://schema.org/WriteAction';
  userInteractionCount: number;
};

type Organization = {
  '@type': 'Organization';
  name: string;
};

type MainEntity = {
  '@type': ProfileEntityType;
  '@id': string;
  url: string;
  name: string;
  identifier: string;
  image?: string;
  description: string;
  jobTitle?: string;
  worksFor?: Organization;
  interactionStatistic: InteractionCounter;
};

export type ProfilePageJsonLd = {
  '@context': 'https://schema.org';
  '@type': 'ProfilePage';
  dateModified?: string;
  mainEntity: MainEntity;
};

export const buildProfilePageJsonLd = ({
  profileUrl,
  name,
  userId,
  description,
  thumbnailUrl,
  totalCount,
  dateModified,
  mentor,
  entityType = 'Person',
}: BuildProfilePageJsonLdParams): ProfilePageJsonLd => {
  const mainEntity: MainEntity = {
    '@type': entityType,
    '@id': profileUrl,
    url: profileUrl,
    name,
    identifier: String(userId),
    description,
    interactionStatistic: {
      '@type': 'InteractionCounter',
      interactionType: 'https://schema.org/WriteAction',
      userInteractionCount: totalCount,
    },
  };

  if (thumbnailUrl) {
    mainEntity.image = thumbnailUrl;
  }

  if (mentor?.job) {
    mainEntity.jobTitle = mentor.job;
  }

  if (mentor?.companyName) {
    mainEntity.worksFor = {
      '@type': 'Organization',
      name: mentor.companyName,
    };
  }

  const jsonLd: ProfilePageJsonLd = {
    '@context': 'https://schema.org',
    '@type': 'ProfilePage',
    mainEntity,
  };

  if (dateModified) {
    jsonLd.dateModified = dateModified;
  }

  return jsonLd;
};

/**
 * JSON-LD 객체를 <script> 안에 안전하게 넣을 수 있는 문자열로 직렬화합니다.
 * `<` 를 유니코드 이스케이프하여 `</script>` 등으로 스크립트 컨텍스트를 탈출하지 못하도록 합니다.
 */
export const serializeJsonLd = (jsonLd: object): string =>
  JSON.stringify(jsonLd).replace(/</g, '\\u003c');

/**
 * 항목 목록(게시글·블로그 등) 중 가장 최신 createdAtUTC(UTC ISO)을 반환합니다.
 * dateModified 용. ISO 8601 UTC 문자열은 사전식(lexicographic) 비교로 시점 비교가 가능합니다.
 */
export const getLatestCreatedAtUTC = (
  items?: ReadonlyArray<{ createdAtUTC?: string | null }>,
): string | undefined => {
  if (!items || items.length === 0) {
    return undefined;
  }

  return items.reduce<string | undefined>((latest, item) => {
    if (!item.createdAtUTC) {
      return latest;
    }

    if (!latest || item.createdAtUTC > latest) {
      return item.createdAtUTC;
    }

    return latest;
  }, undefined);
};
